import { z } from 'zod';
import { RmaProvider, ShipmentMethod } from '../../enums/crew.js';
import { ShippingApiProvider } from '../../enums/enum.js';
import { TrackingStatusCode } from '../../enums/tracking.js';
import { enumToZodLiteralUnion } from '../../zodExtensions.js';
import { locationAddress } from './address.js';
import { claimLineItem } from './claimLineItem.js';
import { rate, returnShipmentMethod } from './shipmentRate.js';
import { carrier } from './tracking.js';
// ? new shipment related types
export const shipmentCreate = z.object({
    claimId: z.number(),
    returnShipment: returnShipmentMethod,
});
export const returnShipmentTracker = z.object({
    latestStatus: enumToZodLiteralUnion(TrackingStatusCode),
    carrier,
});
export const customInstructions = z.object({
    fileUrl: z.url().optional(),
    fileUrlCta: z.string().optional(),
    parcelName: z.string().optional(),
});
const baseShipment = z.object({
    returnShipmentId: z.number(),
    createdOn: z.string(),
    shipmentExpiresOn: z.string(),
    toAddress: locationAddress,
    fromAddress: locationAddress,
    isReturnShipment: z.boolean(),
    customInstructions: customInstructions.optional(),
    returnShipmentPdfAssetUrl: z.string().optional(),
    claimLineItems: z.array(claimLineItem),
});
const packingSlip = baseShipment.extend({
    returnShipmentType: z.literal(ShipmentMethod.packingSlip),
});
const labelShipment = baseShipment.extend({
    returnShipmentType: z.literal(ShipmentMethod.label),
    provider: z.enum(ShippingApiProvider).optional(),
    tracker: returnShipmentTracker.optional(),
    inTransitOn: z.string().optional(),
    carrierName: z.string().optional(),
    carrierServiceName: z.string().optional(),
    carrierTrackingCode: z.string().optional(),
    qrCodeDetail: z
        .object({
        url: z.string(),
        returnToName: z.string(),
        carrier: z.string(),
        carrierIconImgUrl: z.string(),
    })
        .optional(),
    rmaId: z.string().optional(),
    rmaNumber: z.string().optional(),
    rmaProvider: z.enum(RmaProvider).optional(),
});
const consolidatedDropoffShipment = baseShipment
    .pick({
    createdOn: true,
    returnShipmentId: true,
    fromAddress: true,
    shipmentExpiresOn: true,
    claimLineItems: true,
})
    .extend({
    returnShipmentType: z.literal(ShipmentMethod.consolidatedDropoff),
    provider: z.literal(ShippingApiProvider.happyReturns),
    // ? Happy returns QR code
    returnShipmentPdfAssetUrl: z.string(),
    consolidatedDropoffCode: z.string(),
    isReturnShipment: z.literal(true),
    appleWalletPassUrl: z.string().optional(),
    googleWalletPassUrl: z.string().optional(),
});
export const shipment = z.discriminatedUnion('returnShipmentType', [
    packingSlip,
    labelShipment,
    consolidatedDropoffShipment,
]);
// used for quoting "new shipping estimates" on a new order
export const shipmentQuote = z.object({
    id: z.string().nullable(),
    carrier: z.string().nullable(),
    cost: z.number().nullable(),
});
export const shipmentQuoteBody = z.object({
    fromCustomer: returnShipmentMethod.array(),
    toCustomer: rate.nullable(),
});
const shipmentAddress = z.object({
    toAddressId: z.number(),
    fromAddressId: z.number(),
});
export const adminShippingLabel = shipmentAddress.merge(z.object({
    claimId: z.number(),
    returnShipmentType: z.literal(ShipmentMethod.label),
    rate,
    shipmentIdFromPlatform: z.string(),
}));
export const adminPackingSlip = shipmentAddress.merge(z.object({
    claimId: z.number(),
    returnShipmentType: z.literal(ShipmentMethod.packingSlip),
}));
// a consolidated dropoff has no destination, items are consolidated at the return bar
const adminConsolidatedDropoff = z.object({
    claimId: z.number(),
    returnShipmentType: z.literal(ShipmentMethod.consolidatedDropoff),
});
export const shipmentAdminCreate = z.discriminatedUnion('returnShipmentType', [
    adminPackingSlip,
    adminShippingLabel,
    adminConsolidatedDropoff,
]);
export const shipmentAdminQuote = shipmentAddress.merge(z.object({
    weightInOunces: z.number(),
    parcel: z
        .object({
        length: z.number(),
        width: z.number(),
        height: z.number(),
    })
        .optional(),
}));
const shipmentAdminSuccess = z.object({
    kind: z.literal('success'),
    shipmentIdFromPlatform: z.string(),
    rates: z.array(rate),
});
const shipmentAdminFailure = z.object({
    kind: z.literal('failure'),
    errors: z.string().array(),
});
export const shipmentAdminQuoteAndBuy = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('EasyPost_Vesyl'),
        toAddressId: z.number(),
        fromAddressId: z.number(),
    }),
    z.object({
        kind: z.literal('Global_E'),
        globalEOrderId: z.string(),
    }),
]);
export const shipmentAdminQuoteResponse = z.discriminatedUnion('kind', [
    shipmentAdminSuccess,
    shipmentAdminFailure,
]);
//# sourceMappingURL=shipment.js.map